import React, { useReducer, useState, useCallback } from "react";
import "./App.css";

import fishList from "./fish.json";
import bugList from "./bugs.json";
import Clouds from "./clouds/Clouds";
import HorizontalGrid from "horizontal-grid/HorizontalGrid";
import CritterTile from "critter-tile/CritterTile";

import styles from "./App.module.css";
import { useClock } from "hooks/useClock";

const NORTHERN_HEMISPHERE = "northern";
const SOUTHERN_HEMISPHERE = "southern";
const INSECT_MODE = "bugs";
const FISH_MODE = "fish";
const UPDATE_MODE = "update-mode";
const UPDATE_COMPLETED = "update-completed";

const initialState = {
  mode: localStorage.getItem("crittermode") || FISH_MODE,
  completeFish: JSON.parse(localStorage.getItem("complete_fish")) || [],
  completeInsects: JSON.parse(localStorage.getItem("complete_insects")) || [],
};

function reducer(state, action) {
  const { mode, completeFish, completeInsects } = state;
  if (action.type === UPDATE_MODE) {
    localStorage.setItem("crittermode", action.value);
    return { ...state, mode: action.value };
  }
  if (action.type === UPDATE_COMPLETED) {
    if (mode === FISH_MODE) {
      if (completeFish.includes(action.value)) {
        const newComplete = completeFish.filter((e) => e !== action.value);
        localStorage.setItem("complete_fish", JSON.stringify(newComplete));
        return { ...state, completeFish: newComplete };
      }
      const newComplete = [...completeFish, action.value];
      localStorage.setItem("complete_fish", JSON.stringify(newComplete));
      return { ...state, completeFish: newComplete };
    }
    if (mode === INSECT_MODE) {
      if (completeInsects.includes(action.value)) {
        const newComplete = completeInsects.filter((e) => e !== action.value);
        localStorage.setItem("complete_insects", JSON.stringify(newComplete));
        return { ...state, completeInsects: newComplete };
      }
      const newComplete = [...completeInsects, action.value];
      localStorage.setItem("complete_insects", JSON.stringify(newComplete));
      return { ...state, completeInsects: newComplete };
    }
  }
}

function App() {
  const [mode, setMode] = useState(0);
  const [hemisphere, setHemisphere] = useState(
    localStorage.getItem("hemisphere") || NORTHERN_HEMISPHERE
  );
  const [state, dispatch] = useReducer(reducer, initialState);

  const now = useClock(60 * 1000);

  const handleHemisphereChange = useCallback((e) => {
    localStorage.setItem("hemisphere", e.target.value);
    setHemisphere(e.target.value);
  }, []);

  const handleToggleClick = useCallback(
    (i) => () => {
      setMode(i);
    },
    []
  );

  const critterList = state.mode === FISH_MODE ? fishList : bugList;

  const complete =
    state.mode === FISH_MODE ? state.completeFish : state.completeInsects;

  return (
    <div className={styles.wrapper}>
      <Clouds />

      <div className={styles.title}>
        <div className={styles.tabsHolder}>
          {state.mode === INSECT_MODE && (
            <div className={styles.tabsNameBugs}>Insects</div>
          )}
          <div
            className={
              state.mode === INSECT_MODE ? styles.bugActive : styles.bug
            }
            onClick={() => dispatch({ type: UPDATE_MODE, value: INSECT_MODE })}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              viewBox="0 0 85.22 71.99"
            >
              <path d="M42.62,19.69c1.28-4.16,2-7,3-9.7A15.18,15.18,0,0,1,61.28.05c1.85.16,3.87.32,4.09,2.74.24,2.77-1.92,3.08-4,3.42-8.67,1.47-11.74,5.27-10.6,13.62,2.74-1.78,5.31-3.55,8-5.16,8.5-5.13,17.42-5.07,22.68.1,4.79,4.7,5,14.19.63,22.42-2.82,5.31-7.43,8.94-11.91,12.6C67.91,51.66,67.48,53,69,55.5a15.06,15.06,0,0,1,1.55,5.22c1.06,6.37-3,11.22-9.46,11.15-6.69-.07-11.75-3.53-15.45-8.75-2.41-3.38-3.83-3.11-6,.1a19.26,19.26,0,0,1-8.76,7.17c-4.69,1.92-9.37,2.73-13.52-1.13S14,60.62,16,56.34c1.62-3.39,1-5-1.78-7.26C8.08,44.2,2.29,38.87.68,30.59-.47,24.67-.67,18.87,4.24,14.38c5.23-4.78,11.33-3.91,17.4-2.05C26.42,13.8,30,17.5,34.57,19.7c.79-8.21-2.33-12.39-9.84-13.44C22.44,5.94,19.5,6.08,20,2.69,20.39-.47,23.32,0,25.58.1c7,.21,11.87,3.79,14.23,10.46C40.71,13.12,41.41,15.75,42.62,19.69Z" />
            </svg>
          </div>
          {state.mode === FISH_MODE && (
            <div className={styles.tabsNameFish}>Fish</div>
          )}
          <div
            className={
              state.mode === FISH_MODE ? styles.fishActive : styles.fish
            }
            onClick={() => dispatch({ type: UPDATE_MODE, value: FISH_MODE })}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="26"
              viewBox="0 0 57.21 33.13"
            >
              <path
                class="cls-1"
                d="M56.55,7.1c1.74-2.9-.17-3.89-3-3.47-3.52.53-5.42,3.58-8,5.48C34.84-.48,21.2-2.59,8.54,3.23-.69,7.48-2.73,16.75,3.86,24.48a24.57,24.57,0,0,0,32,4.75c3.1-1.95,5.45-4.73,8.22-7.29,3,2.73,5,6.23,9,7.22,1.27.32,2.75.62,3.72-.57.69-.83.27-1.95-.27-2.8C52.53,19.54,52.72,13.45,56.55,7.1ZM9.66,16.33A4,4,0,0,1,6,12.14a4,4,0,0,1,3.7-4.2,4,4,0,0,1,3.71,4.2A4,4,0,0,1,9.66,16.33Z"
              />
            </svg>
          </div>
        </div>
        {/* <h1>Critterpedia</h1> */}

        <div className={styles.toggles}>
          <div className="toggleWrapper">
            <div
              className={`toggle ${mode === 0 ? "activeToggle" : ""}`}
              onClick={handleToggleClick(0)}
            >
              Active this hour
            </div>
          </div>
          <div className="toggleWrapper">
            <div
              className={`toggle ${mode === 1 ? "activeToggle" : ""}`}
              onClick={handleToggleClick(1)}
            >
              Leaving next month
            </div>
          </div>
          <div className="toggleWrapper">
            <div
              className={`toggle ${mode === 2 ? "activeToggle" : ""}`}
              onClick={handleToggleClick(2)}
            >
              New this month
            </div>
          </div>
        </div>

        <select
          className={styles.hemisphereSelect}
          defaultValue={hemisphere}
          onChange={handleHemisphereChange}
        >
          <option value={NORTHERN_HEMISPHERE}>Northern</option>
          <option value={SOUTHERN_HEMISPHERE}>Southern</option>
        </select>
      </div>

      <HorizontalGrid
        rows={5}
        itemCount={critterList.length}
        tileForIndex={(i, lastRow, lastCol) => {
          return (
            <CritterTile
              onClick={() => dispatch({ type: UPDATE_COMPLETED, value: i })}
              critter={critterList[i]}
              hemisphere={hemisphere}
              lastRow={lastRow}
              lastCol={lastCol}
              isComplete={complete.includes(i)}
              now={now}
              mode={mode}
            />
          );
        }}
      />
    </div>
  );
}

export default App;
